import { FC } from "react";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor, Group, Stack, Text, Tooltip } from "@mantine/core";
import { usePrefab } from "@prefab-cloud/prefab-cloud-react";

import { Issue } from "@protos/issues/v1/issues";
import { Reachability } from "@protos/sca/v1/sca";

import { useScaRecommendedDependencyUpdate } from "../../../hooks/useScaRecommendedDependencyUpdate";

export interface RemediationInfoProps {
  finding: Issue;
}

export const RemediationInfo: FC<RemediationInfoProps> = ({ finding }) => {
  const { get: getPrefabVal } = usePrefab();
  const turnOffLockfileUrl = getPrefabVal(
    "sca.turn_off_lockfile_links"
  ) as boolean;
  const reachability = finding.scaAttributes?.reachability;
  const dependencyLockfileUrl =
    finding.scaAttributes?.foundDependency?.lockfileLineUrl;
  const recommendedUpdate = useScaRecommendedDependencyUpdate(finding);

  return (
    <Stack gap={5} style={{ marginBottom: 12 }}>
      <Text size="md" fw={500} c="dark.9">
        Remediation
      </Text>
      {reachability === Reachability.ALWAYS_REACHABLE && (
        <Text>
          Semgrep recommends patching this vulnerability as soon as possible.
        </Text>
      )}
      {reachability === Reachability.CONDITIONALLY_REACHABLE && (
        <Text>
          Validate whether this meets the reachable condition.&nbsp;
          {recommendedUpdate &&
            `If it does, patch to version ${recommendedUpdate.version}.`}
        </Text>
      )}
      <Group>
        <Stack gap={0}>
          {dependencyLockfileUrl && !turnOffLockfileUrl ? (
            <Tooltip
              label="See where this version is set"
              position="bottom-start"
              withArrow={false}
              withinPortal
            >
              <Anchor
                href={dependencyLockfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                lineClamp={1}
                fw={400}
              >
                <Text inherit fz="md">
                  {finding.scaAttributes?.foundDependency?.version}&nbsp;
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="xs" />
                </Text>
              </Anchor>
            </Tooltip>
          ) : (
            <Tooltip
              label="A link to this lockfile is not available because the dependency was detected in a local scan."
              position="bottom-start"
              withArrow={false}
              withinPortal
            >
              <Text fz="md" fw={400}>
                {finding.scaAttributes?.foundDependency?.version}
              </Text>
            </Tooltip>
          )}
          <Text fz="xs" c="gray.7" fw={600}>
            YOUR VERSION
          </Text>
        </Stack>
        <Stack gap={0}>
          <Text fz="md">
            {recommendedUpdate
              ? recommendedUpdate.version
              : "No patch available"}
          </Text>
          <Text fz="xs" c="gray.7" fw={600}>
            RECOMMENDED PATCH
          </Text>
        </Stack>
      </Group>
    </Stack>
  );
};
